// frontend/src/components/common/TablaPrimitivas.js
//
// El lenguaje visual de las tablas del sistema — el mismo que aplica
// ListaUnificada en su variant "rows" — pero como styled sueltos.
//
// POR QUÉ EXISTE ESTE ARCHIVO
//
// ListaUnificada es dueña de su fetch: recibe un `endpoint` y arma ella misma
// page / page_size / search / ordering contra el servidor. Eso la hace ideal
// para un listado que es "una tabla del backend" y la vuelve inaplicable
// cuando las filas NO salen de un endpoint:
//
//   - Stock de Almacén: cruce en cliente de stock + repuestos + obras, con
//     filas agregadas por repuesto ("Ver por obra") y ubicación editable.
//   - Movimientos: filas agrupadas por número de movimiento.
//   - Demanda: cálculo cruzado OT / stock / requerimientos, con filas que se
//     expanden en sub-filas por OT.
//   - Logística y Flota: datos ya en memoria vía useListaHidratada.
//
// Esas pantallas necesitan el ASPECTO de ListaUnificada sin su MOTOR. Antes de
// este archivo cada una lo resolvía copiando los mismos ~40 renglones de CSS:
// el lenguaje visual llegó a estar escrito cuatro veces (ListaUnificada,
// common/Table.jsx, ListaLogistica y AlmacenPage), y cada ajuste de diseño
// había que acordarse de replicarlo en las cuatro. Acá está una sola vez.
//
// CÓMO SE USA
//
//   import { TablaBase, TablaScroll, TablaScrollEnmarcada } from './TablaPrimitivas';
//   const MiTabla = styled(TablaBase)`table-layout: fixed;`;
//
// Las variantes propias de cada pantalla (anchos de columna, celdas que
// envuelven texto) se construyen encima con styled(TablaBase) — nunca
// redefiniendo padding, tipografía de cabecera o color de borde, que son
// justamente lo que este archivo unifica.
//
// PENDIENTE: ListaUnificada, common/Table.jsx y ListaLogistica todavía tienen
// su copia. Migrarlas a estas primitivas es un cambio visualmente neutro pero
// toca pantallas que ya funcionan, así que va por separado y con su propia
// verificación.

import styled, { css } from 'styled-components';

// Cabecera: 11px, mayúsculas y color apagado. Es lo que más distingue de un
// <th> por defecto y lo que hace que dos tablas se lean como el mismo sistema.
export const tipografiaCabeceraTabla = css`
  padding: 6px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.colors.txtMuted};
  background: ${({ theme }) => theme.colors.bgPrincipal};
  white-space: nowrap;
`;

// Celda: 12px sobre el color de texto principal, con el padding ajustado para
// que la fila quede compacta sin apretarse.
export const tipografiaCeldaTabla = css`
  padding: 7px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  vertical-align: middle;
  line-height: 1.3;
`;

/**
 * Tabla con el lenguaje visual unificado.
 *
 * Incluye el patrón de acciones por fila que usa ListaUnificada: los botones
 * marcados con `.accion-fila` están ocultos y sólo aparecen cuando el puntero
 * entra en la fila, para que la tabla se lea limpia. En pantallas táctiles
 * —donde no hay hover y esperar a que "aparezcan" sería esperarlos para
 * siempre— quedan visibles.
 *
 * `.stock-action` es el nombre que traía AlmacenPage. Se mantiene como alias
 * para no tocar 24 llamadas en la misma pasada en que cambia el aspecto.
 */
export const TablaBase = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgPrincipal};

  th {
    ${tipografiaCabeceraTabla}
    position: sticky;
    top: 0;
    z-index: 1;
    border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
    user-select: none;
  }

  td {
    ${tipografiaCeldaTabla}
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  }

  tbody tr {
    transition: background-color 0.15s ease-in-out;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr .accion-fila,
  tbody tr .stock-action {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }

  tbody tr:hover .accion-fila,
  tbody tr:focus-within .accion-fila,
  tbody tr:hover .stock-action,
  tbody tr:focus-within .stock-action {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    tbody tr .accion-fila,
    tbody tr .stock-action {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

/**
 * Contenedor con scroll, SIN marco: la tabla es la superficie del panel, igual
 * que en ListaUnificada variant "rows". Es el que va en un listado a pantalla
 * completa, donde un recuadro alrededor sólo agrega una línea de más.
 */
export const TablaScroll = styled.div`
  overflow: auto;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  scrollbar-gutter: stable both-edges;
`;

/**
 * Contenedor con scroll y marco. Va dentro de un modal, donde la tabla es una
 * sección más del formulario y necesita un borde que la delimite del resto.
 */
export const TablaScrollEnmarcada = styled(TablaScroll)`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px;
`;
